import React, { Component, Suspense, lazy } from 'react';
import { Spin } from 'antd';
import { getSlotLoader, getManifest } from './hostApi';
import { ensureMarketplaceAppsLoaded } from './loader';
import { getMarketplaceContributions } from '../apps/owner-reporting/api/marketplace';

class SlotErrorBoundary extends Component {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'App slot failed' };
  }

  componentDidCatch(error) {
    // Keep host page alive — log only
    // eslint-disable-next-line no-console
    console.error('[MarketplaceSlot]', error);
  }

  render() {
    if (this.state.hasError) {
      return null; // hide broken slot; host page unaffected
    }
    return this.props.children;
  }
}

const lazyCache = new Map();
const getLazyComponent = (componentKey, loader) => {
  if (!lazyCache.has(componentKey)) {
    lazyCache.set(componentKey, lazy(loader));
  }
  return lazyCache.get(componentKey);
};

/**
 * Renders marketplace UI contributions for a host page (e.g. page="unit").
 * Failures degrade to empty — never block the host page.
 */
class MarketplaceSlot extends Component {
  state = { contributions: [], loading: true };

  componentDidMount() {
    ensureMarketplaceAppsLoaded();
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.page !== this.props.page || prevProps.hostId !== this.props.hostId || prevProps.unitId !== this.props.unitId) {
      this.load();
    }
  }

  load = async () => {
    const { page, hostId } = this.props;
    if (!hostId) {
      this.setState({ contributions: [], loading: false });
      return;
    }
    try {
      let contributions = await getMarketplaceContributions({ page, hostId });
      contributions = Array.isArray(contributions) ? contributions : [];

      // Fallback: if API returns nothing (stale DB manifest), use FE-registered manifests
      // only for apps that already have a registered slot component.
      if (!contributions.length) {
        ensureMarketplaceAppsLoaded();
        const local = [];
        // owner-reporting is the only v1 app; keep generic via getManifest when more apps land
        const manifest = getManifest('owner-reporting');
        (manifest && manifest.uiContributions ? manifest.uiContributions : [])
          .filter(c => !page || c.page === page)
          .forEach(c => {
            if (getSlotLoader(c.componentKey)) {
              local.push({ ...c, appCode: 'owner-reporting' });
            }
          });
        contributions = local;
      }

      this.setState({ contributions, loading: false });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[MarketplaceSlot] contributions failed', err);
      this.setState({ contributions: [], loading: false });
    }
  };

  renderContribution = contribution => {
    const loader = getSlotLoader(contribution.componentKey);
    if (!loader) {
      // eslint-disable-next-line no-console
      console.warn('[MarketplaceSlot] Unknown componentKey', contribution.componentKey);
      return null;
    }
    const LazyComp = getLazyComponent(contribution.componentKey, loader);
    const { unitId, hostId, context = {} } = this.props;
    return (
      <SlotErrorBoundary key={contribution.slotId || contribution.componentKey}>
        <Suspense fallback={<Spin size="small" />}>
          <LazyComp
            title={contribution.title}
            unitId={unitId}
            hostId={hostId}
            contribution={contribution}
            {...context}
          />
        </Suspense>
      </SlotErrorBoundary>
    );
  };

  render() {
    const { loading, contributions } = this.state;
    const { placement } = this.props;

    if (loading) return null;
    const filtered = placement ? contributions.filter(c => c.placement === placement) : contributions;
    if (!filtered.length) return null;

    return <div className="marketplace-slots">{filtered.map(this.renderContribution)}</div>;
  }
}

export default MarketplaceSlot;
